/**
 * Link-in-bio editor helpers (pure, unit-tested): block reordering, schedule
 * visibility for the phone preview, datetime-local conversion, analytics
 * formatting and CSV export. Mirrors the server rules in
 * `backend/servers/social/services/bio_pages.py` where they overlap.
 *
 * @module lib/bioModel
 */

import { type CsvCell, toCsv } from "@plerity/csv";
import type { BioAnalyticsGroup, BioAnalyticsRow, BioLink, BioTheme } from "../api/bioPages";

/** Same format as the DB CHECK on bio_pages.slug. */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]{2,39}$/;

// hardcoded-colors-exempt: default THEME DATA for a client's public link-in-bio page
// (rendered server-side into its own HTML, outside this app's token system).
export const DEFAULT_THEME: BioTheme = {
  primary: "#111827",
  background: "#ffffff",
  text: "#111827",
  button_style: "rounded",
  button_variant: "solid",
  font_heading: "",
  font_body: "",
  dark_mode: "auto",
};

export const KIND_LABELS: Record<BioLink["kind"], string> = {
  button: "Button link",
  header: "Section header",
  social_icon: "Social profile",
  text: "Text",
};

/** Move the item at `from` to `to` (returns a new array; out-of-range = unchanged). */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return [...items];
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}

/** Is the block shown on the live page right now? (active + inside its window). */
export function isLinkVisible(link: Pick<BioLink, "is_active" | "starts_at" | "ends_at">, now: Date): boolean {
  if (!link.is_active) return false;
  if (link.starts_at && new Date(link.starts_at).getTime() > now.getTime()) return false;
  if (link.ends_at && new Date(link.ends_at).getTime() <= now.getTime()) return false;
  return true;
}

export const SCHEDULE_LABELS: Record<BioLink["schedule_state"], string> = {
  live: "Live",
  scheduled: "Scheduled",
  expired: "Ended",
  off: "Hidden",
};

/** ISO timestamp → `<input type="datetime-local">` value in the browser's zone. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `<input type="datetime-local">` value → ISO (UTC) or null when empty/invalid. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** CTR (0-1 ratio) → "12.5%"; "—" with no views. */
export function formatCtr(ctr: number | null | undefined): string {
  if (ctr === null || ctr === undefined) return "—";
  return `${(ctr * 100).toFixed(1).replace(/\.0$/, "")}%`;
}

/** YYYY-MM-DD for a Date (UTC — analytics days are UTC). */
export function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The last `days` UTC days ending today. */
export function defaultRange(days = 30, today: Date = new Date()): { start: string; end: string } {
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const start = new Date(end.getTime() - (days - 1) * 86_400_000);
  return { start: isoDay(start), end: isoDay(end) };
}

const GROUP_HEADER: Record<BioAnalyticsGroup, string> = { day: "date", link: "link", source: "source" };

/**
 * Analytics rows → export table (header + one row each). Serialise with
 * `@plerity/csv` (`toCsv` / `downloadCsv`), which neutralises spreadsheet
 * formulas in client-entered link labels.
 */
export function analyticsTable(
  group: BioAnalyticsGroup,
  rows: readonly BioAnalyticsRow[]
): { headers: string[]; rows: CsvCell[][] } {
  return {
    headers: [GROUP_HEADER[group], "views", "clicks", "unique_visitors", "ctr_percent"],
    rows: rows.map((r) => [
      r.label,
      r.views,
      r.clicks,
      r.uniques,
      r.ctr === null ? null : Number((r.ctr * 100).toFixed(2)),
    ]),
  };
}

/** Analytics rows → CSV text. */
export function analyticsCsv(group: BioAnalyticsGroup, rows: readonly BioAnalyticsRow[]): string {
  const table = analyticsTable(group, rows);
  return toCsv(table.headers, table.rows);
}

/** Black or white text on a hex background (WCAG luminance) — same as the server. */
export function readableTextOn(hex: string): string {
  const value = /^#[0-9a-f]{6}$/i.test(hex) ? hex.slice(1) : "111827";
  const channels = [0, 2, 4].map((i) => Number.parseInt(value.slice(i, i + 2), 16) / 255);
  const linear = channels.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const luminance = 0.2126 * (linear[0] ?? 0) + 0.7152 * (linear[1] ?? 0) + 0.0722 * (linear[2] ?? 0);
  // @allow-color: WCAG contrast pick for user-chosen theme colours (data, not app chrome)
  return luminance > 0.179 ? "#111111" : "#ffffff";
}

/** Only http(s) links with a host and no credentials (the server enforces the full rule). */
export function urlProblem(url: string): string | null {
  const value = url.trim();
  if (!value) return "Add the link's web address.";
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return "Enter a full web address starting with https://";
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:")
    return "Only http:// and https:// links are allowed.";
  if (parsed.username || parsed.password) return "Links with a username or password aren't allowed.";
  if (!parsed.hostname.includes(".")) return "That address has no website domain.";
  return null;
}
